import {FC, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import {WebAppInfoHotel, WebappInfo} from '../../../components/WebappInfo/WebappInfo'
import useModals from '../../../hooks/useModals'
import {GET} from '../../../services/api'
import {i18n} from '../../../translate/i18n'
import useAuth from '../../../hooks/useAuth'
import {getPlugintypeIdByCode} from '../../../functions/plugintype'
import Swal from 'sweetalert2'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import usePluginsSpace from '../../../hooks/usePluginSpace'

/**
 * ============================
 * WEBAPP COMPONENT - ORGANIZADO POR SETORES
 * ============================
 *
 * Este arquivo foi organizado em setores para melhor manutenção:
 *
 * 1. TIPOS COMUNS - Tipos utilizados por todos os plugins
 * 2. TIPOS ESPECÍFICOS HOSPITALITY - Tipos exclusivos do setor hospitality
 * 3. HOOKS E ESTADOS COMUNS - Estados e hooks compartilhados
 * 4. ESTADOS ESPECÍFICOS HOSPITALITY - Estados exclusivos do hospitality
 * 5. FUNÇÕES COMUNS - Funções utilizadas por todos os plugins
 * 6. FUNÇÕES ESPECÍFICAS HOSPITALITY - Lógica exclusiva do hospitality
 * 7. FUNÇÕES ESPECÍFICAS OUTROS PLUGINS - Lógica para aggregator, restaurant, etc
 * 8. COMPONENTE PRINCIPAL - Renderização e orquestração
 *
 * Benefícios desta organização:
 * - Facilita manutenção por separar responsabilidades
 * - Permite carregamento condicional de funcionalidades
 * - Reduz acoplamento entre diferentes tipos de plugin
 * - Melhora legibilidade e testabilidade do código
 */

// ============================
// TIPOS COMUNS (TODOS OS PLUGINS)
// ============================

export type WebappButtonType = {
  text: string
  color: string
  icon: string
  action: (value?: any) => any
}

type WebappType = {
  id: number
  title: string
  buttons: WebappButtonType[]
  webappLink: string
  type: string
}

type WebappResponseType = {
  id: number
  name: string
  type: string
  hash: string
  pluginSpace: {
    subdomain: string
  }
}

type QRCodeType = {
  type: string
  label: string
  iniInterval: number | undefined
  endInterval: number | undefined
  intervals: {
    iniInterval: number | undefined
    endInterval: number | undefined
  }[]
  url: string
  hash?: string
}

const initialQRCode: QRCodeType = {
  type: '',
  label: '',
  iniInterval: 1,
  endInterval: 1,
  intervals: [],
  url: '',
  hash: '',
}

type ModalityType = {
  id: number
  code: string
  name: string
  status: boolean
}

type Select = {
  label: string
  value: string
}

// ============================
// TIPOS ESPECÍFICOS HOSPITALITY
// ============================

type ButtomSvgWebApp = {
  id: number
  name: string
  icon: string
  svg?: any
  auth: number
  routeType: string
  routeContent: string
  status: boolean
  background?: any
  color?: any
  order: number
  appConfigId: number
  pluginspaceId: number
  idSvgwebApp: number
  svgWebApp?: SvgWebApp
  svgWebApp2?: string
}

type SvgWebApp = {
  id: number
  svg: string
  description: string
  category: string
}

type Icon = {
  id: number
  svg: string
  description: string
  category: string
}

type HotelStore = {
  id: number
  pluginspaceId: number
  code: string
  description: string
  status: boolean
  img: string | null
  pluginspace?: {
    name: string
    hash: string
    subdomain: string
  }
}

// ============================
// HOOKS E ESTADOS COMUNS
// ============================

const useCommonWebappState = () => {
  const navigate = useNavigate()
  const {showWrapperModal, closeWrapperModal, showDeleteModal} = useModals()
  const {getPluginSpace, usingSynkar} = usePluginsSpace()
  const [loading, setLoading] = useState(false)
  const {loginInfo} = useAuth()

  const [webapps, setWebapps] = useState<WebappType[]>([])
  const [qrCode, setQRCode] = useState<QRCodeType>(initialQRCode)
  const [iniInterval, setIniInterval] = useState(0)
  const [endInterval, setEndInterval] = useState(0)
  const [initialQR, setInitialQR] = useState(1)
  const [finalQR, setFinalQR] = useState(1)
  const [maxRange, setMaxRange] = useState(false)
  const [generateQr, setGenerateQr] = useState(true)

  return {
    navigate,
    showWrapperModal,
    closeWrapperModal,
    showDeleteModal,
    getPluginSpace,
    usingSynkar,
    loading,
    setLoading,
    loginInfo,
    webapps,
    setWebapps,
    qrCode,
    setQRCode,
    iniInterval,
    setIniInterval,
    endInterval,
    setEndInterval,
    initialQR,
    setInitialQR,
    finalQR,
    setFinalQR,
    maxRange,
    setMaxRange,
    generateQr,
    setGenerateQr,
  }
}

// ============================
// ESTADOS ESPECÍFICOS HOSPITALITY
// ============================

const useHospitalityState = () => {
  const [typeButton, setTypeButton] = useState<Select>()
  const [listClassification, setListClassification] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [categoryId, setCategoryId] = useState<Select>()
  const [buttonName, setButtonName] = useState<string>()
  const [externalLink, setExternalLink] = useState<string>()
  const [buttonsWebApp, setButtonsWebApp] = useState<ButtomSvgWebApp[]>([])
  const [buttonApp, setButtonApp] = useState<number>()
  const [icons, setIcons] = useState<Icon[]>([])
  const [iconSelected, setIconSelected] = useState<number>()
  const [id, setId] = useState<number | null>(null)
  const [hotelStores, setHotelStores] = useState<HotelStore[]>([])
  const [selectedStore, setSelectedStore] = useState<HotelStore | null>(null)

  return {
    typeButton,
    setTypeButton,
    listClassification,
    setListClassification,
    categories,
    setCategories,
    categoryId,
    setCategoryId,
    buttonName,
    setButtonName,
    externalLink,
    setExternalLink,
    buttonsWebApp,
    setButtonsWebApp,
    buttonApp,
    setButtonApp,
    icons,
    setIcons,
    iconSelected,
    setIconSelected,
    id,
    setId,
    hotelStores,
    setHotelStores,
    selectedStore,
    setSelectedStore,
  }
}

// ============================
// FUNÇÕES COMUNS
// ============================

const useCommonFunctions = (commonState: ReturnType<typeof useCommonWebappState>) => {
  const {
    // getPluginSpace,
    // setLoading,
    // loginInfo,
    // setWebapps,
    // usingSynkar,
    qrCode,
    setQRCode,
    initialQR,
    finalQR,
    // setFinalQR,
    // maxRange,
    setMaxRange,
  } = commonState

  const [sectorOptions, setSectorOptions] = useState([
    {select: 'Selecione um setor', value: ''},
    {select: 'Comanda', value: 'card'},
    {select: 'Mesa', value: 'table'},
  ])

  const [actions, setActions] = useState<
    'generateQrcode' | 'alert' | 'editButton' | 'createButton' | 'buttonIcone' | 'hotelStores'
  >('generateQrcode')

  const getModalities = async (): Promise<ModalityType[]> => {
    const modalitiesRes = await GET('/location')
    if (modalitiesRes && modalitiesRes.data) {
      return modalitiesRes.data
    } else {
      return []
    }
  }

  const showGenerateQRCode = (url: string, type: string, hash?: string) => {
    setQRCode({...initialQRCode, type, url, hash: hash ? hash : ''})
    setActions('generateQrcode')
    commonState.showWrapperModal('QR Code')
  }

  const addRange = () => {
    let _qrCode = {...qrCode}
    if (_qrCode.intervals.length > 13) {
      setMaxRange(true)
    } else if (_qrCode.intervals.length <= 13) {
      _qrCode.intervals.push({iniInterval: initialQR, endInterval: finalQR})
      setQRCode(_qrCode)
    }
  }

  const removeRange = (i: number) => {
    let _qrCode = {...qrCode}
    if (_qrCode.intervals.length <= 14) setMaxRange(false)
    if (_qrCode.intervals[i]) {
      _qrCode.intervals.splice(i, 1)
      setQRCode(_qrCode)
    }
  }

  const generateQRCode = () => {
    if (!qrCode.type) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        text: 'É necessário selecionar um tipo de QRCode',
      })
    }
    const data = btoa(JSON.stringify(qrCode))
    const encodedData = encodeURIComponent(data)
    if (encodedData.length > 900) {
      const cacheKey = `qr-cache-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      localStorage.setItem(cacheKey, data)
      window.open(`/webapp/generate-qr-code/${cacheKey}`)
    } else {
      window.open(`/webapp/generate-qr-code/${encodedData}`)
    }
  }

  const verifyOptions = (type: string) => {
    if (type === 'table' || type === 'card') {
      return sectorOptions.filter((el) => el.value === type)
    }
    return sectorOptions
  }

  return {
    sectorOptions,
    setSectorOptions,
    actions,
    setActions,
    getModalities,
    showGenerateQRCode,
    addRange,
    removeRange,
    generateQRCode,
    verifyOptions,
  }
}

// ============================
// FUNÇÕES ESPECÍFICAS PARA OUTROS PLUGINS
// ============================

const useOtherPluginsLogic = (
  commonState: ReturnType<typeof useCommonWebappState>,
  commonFunctions: ReturnType<typeof useCommonFunctions>,
  hospitalityFunctions?: {showHotelStoresModal: () => void}
) => {
  const {loginInfo, navigate} = commonState
  const {showGenerateQRCode} = commonFunctions

  const buildWebappButtons = (webapp: WebappResponseType, url: string) => {
    if (webapp.type === 'qr_whole_sale') {
      return [
        {
          text: i18n.t(`webapp.personalize`),
          color: 'primary',
          icon: 'bi bi-pencil-square fs-4',
          action: () => navigate(`/webapp/editar/${webapp.id}`),
        },
        {
          text: i18n.t(`webapp.settings`),
          color: 'info',
          icon: 'bi bi-gear fs-4',
          action: () => navigate(`/webapp/configuracoes_atacado/${webapp.id}`),
        },
      ]
    }

    const plugintypeId = getPlugintypeIdByCode(loginInfo.plugintype)

    // Hospitality (7)
    if (plugintypeId === 7) {
      return [
        {
          text: i18n.t(`webapp.generate_qrcode`),
          color: 'success',
          icon: 'bi bi-qr-code fs-4',
          action: () => hospitalityFunctions?.showHotelStoresModal(),
        },
        {
          text: i18n.t(`webapp.personalize`),
          color: 'primary',
          icon: 'bi bi-pencil-square fs-4',
          action: () => navigate(`/webapp/editar/${webapp.id}`),
        },
        {
          text: i18n.t(`webapp.settings`),
          color: 'info',
          icon: 'bi bi-gear fs-4',
          action: () => navigate(`/webapp/configuracoes/${webapp.id}`),
        },
      ]
    }

    // Restaurant (7)
    if (plugintypeId === 18) {
      return [
        {
          text: i18n.t(`webapp.personalize`),
          color: 'primary',
          icon: 'bi bi-pencil-square fs-4',
          action: () => navigate(`/webapp/editar/${webapp.id}`),
        },
        {
          text: i18n.t(`webapp.generate_qrcode`),
          color: 'success',
          icon: 'bi bi-qr-code fs-4',
          action: () => showGenerateQRCode(url, '', webapp.hash),
        },
        {
          text: i18n.t(`webapp.settings`),
          color: 'info',
          icon: 'bi bi-gear fs-4',
          action: () => navigate(`/webapp/configuracoes/${webapp.id}`),
        },
      ]
    }

    // Aggregator (21)
    if (plugintypeId === 21) {
      return [
        {
          text: i18n.t(`webapp.personalize`),
          color: 'primary',
          icon: 'bi bi-pencil-square fs-4',
          action: () => navigate(`/webapp/editar/${webapp.id}`),
        },
      ]
    }

    // Default for other plugins
    return [
      {
        text: i18n.t(`webapp.personalize`),
        color: 'primary',
        icon: 'bi bi-pencil-square fs-4',
        action: () => navigate(`/webapp/editar/${webapp.id}`),
      },
      {
        text: i18n.t(`webapp.settings`),
        color: 'info',
        icon: 'bi bi-gear fs-4',
        action: () => navigate(`/webapp/configuracoes/${webapp.id}`),
      },
    ]
  }

  const buildWebappUrl = (webapp: WebappResponseType) => {
    // 1. Lógica para Hospitality
    if (loginInfo.plugintype === 'hospitality') {
      if (webapp.type === 'qr') {
        if (process.env.REACT_APP_PROD === '1') {
          return 'http://hospitality.ninegrid.com.br/' + window.btoa(webapp.pluginSpace.subdomain)
        }
        return (
          'http://hospitality-staging.ninegrid.com.br/' + window.btoa(webapp.pluginSpace.subdomain)
        )
      } else {
        if (process.env.REACT_APP_PROD === '1') {
          return 'http://hospitality-os.ninegrid.com.br/'
        }
        return 'http://hospitality-os-stage.ninegrid.com.br/'
      }
    }

    // 2. Lógica para Aggregator
    if (loginInfo.plugintype === 'aggregator') {
      return (
        process.env.REACT_APP_AGGREGATORAPP_URL + '/' + window.btoa(webapp.pluginSpace.subdomain)
      )
    }

    // 3. Lógica para ATACADO
    if (webapp.type === 'qr_whole_sale') {
      return (
        process.env.REACT_APP_WEBAPP_WHOLE_SALE_URL +
        '/' +
        window.btoa(webapp.pluginSpace.subdomain)
      )
    }

    // 4. Lógica padrão (Varejo / Seller)
    if (process.env.REACT_APP_PROD === '1') {
      return (
        'https://webapp.ninegrid.com.br/' +
        window.btoa(webapp.pluginSpace.subdomain) +
        (webapp.type === 'seller' ? '/seller' : '')
      )
    }

    return (
      process.env.REACT_APP_WEBAPP_URL +
      '/' +
      window.btoa(webapp.pluginSpace.subdomain) +
      (webapp.type === 'seller' ? '/seller' : '')
    )
  }

  const buildWebappTitle = (webapp: WebappResponseType, isWholeSaler: boolean) => {
    let title = webapp.name

    if (isWholeSaler) {
      switch (webapp.type) {
        case 'qr_whole_sale':
          title = title + ' - Atacado'
          break
        case 'qr':
          title = title + ' - Varejo'
          break
        case 'seller':
          title = title + ' - Varejo'
          break
      }
    }

    return title
  }

  return {
    buildWebappButtons,
    buildWebappUrl,
    buildWebappTitle,
  }
}

const WebappContent: FC = () => {
  const commonState = useCommonWebappState()
  const hospitalityState = useHospitalityState()
  const commonFunctions = useCommonFunctions(commonState)

  const {
    // navigate,
    showWrapperModal,
    // closeWrapperModal,
    // showDeleteModal,
    loading,
    setLoading,
    loginInfo,
    webapps,
    setWebapps,
    getPluginSpace,
    usingSynkar,
    qrCode,
    setQRCode,
    // iniInterval,
    // setIniInterval,
    // endInterval,
    // setEndInterval,
    // initialQR,
    // setInitialQR,
    // finalQR,
    // setFinalQR,
    maxRange,
    // setMaxRange,
  } = commonState

  const {
    sectorOptions,
    setSectorOptions,
    actions,
    setActions,
    getModalities,
    // showGenerateQRCode,
    addRange,
    removeRange,
    generateQRCode,
    // verifyOptions,
  } = commonFunctions

  // Estados específicos do Hospitality
  // const {
  //   typeButton,
  //   setTypeButton,
  //   listClassification,
  //   setListClassification,
  //   categories,
  //   setCategories,
  //   categoryId,
  //   setCategoryId,
  //   buttonName,
  //   setButtonName,
  //   externalLink,
  //   setExternalLink,
  //   buttonsWebApp,
  //   setButtonsWebApp,
  //   buttonApp,
  //   setButtonApp,
  //   icons,
  //   setIcons,
  //   iconSelected,
  //   setIconSelected,
  //   id,
  //   setId,
  // } = hospitalityState

  // ============================
  // FUNÇÕES ESPECÍFICAS HOSPITALITY
  // ============================

  // const generateClassificationTree = (
  //   categories: any[],
  //   parent: string | null,
  //   name: string
  // ): any => {
  //   const parentCategory = categories.find((category) => category.code === parent)
  //   if (parentCategory)
  //     return `${generateClassificationTree(
  //       categories,
  //       parentCategory.parent,
  //       parentCategory.name
  //     )} > ${name}`
  //   else return name
  // }

  // const getCategoryContent = async () => {
  //   try {
  //     const res = await POST(`/classification/filter`, {
  //       plugintypeId: 10,
  //       status: true,
  //       deleted: 'not show',
  //     })
  //     res.data.map((row: any) => {
  //       listClassification.push({
  //         id: row.id,
  //         parent: row.parent,
  //         code: row.code,
  //         name: generateClassificationTree(res.data, row.parent, row.name),
  //       })
  //     })
  //     const categories = [
  //       {
  //         value: 0,
  //         select: 'Selecione',
  //         key: -1,
  //       },
  //     ]
  //     listClassification.map((row: any, idx) => {
  //       categories.push({
  //         value: row.id,
  //         select: row.name,
  //         key: idx,
  //       })
  //     })
  //     setCategories(categories)
  //   } catch (e) {
  //     console.log(e)
  //   }
  // }

  // const getButtonsWebApp = async () => {
  //   try {
  //     const res = await POST(`/appitems/filter`)

  //     const buttonsSvgWebApp = res.data
  //       .sort((a: any, b: any) => a.order - b.order)
  //       .map((bt: ButtomSvgWebApp) => {
  //         const schema = {...bt}

  //         schema.svgWebApp2 = bt.svgWebApp?.svg ? bt.svgWebApp?.svg : ''

  //         return schema
  //       })

  //     setButtonsWebApp(buttonsSvgWebApp)
  //   } catch (e) {
  //     console.log(e)
  //   }
  // }

  // const getIcons = async () => {
  //   try {
  //     const res = await POST(`/svgwebapp/filter`)

  //     setIcons(res.data)
  //   } catch (e) {
  //     console.log(e)
  //   }
  // }

  const getHotelStores = async () => {
    try {
      const response = await GET('/stores-hotel')
      if (response && response.data) {
        hospitalityState.setHotelStores(response.data)
      }
    } catch (error) {
      console.log(error)
    }
  }

  const showHotelStoresModal = () => {
    getHotelStores()
    hospitalityState.setSelectedStore(null)
    setActions('hotelStores')
    showWrapperModal('Gerar QR Code - Estabelecimento')
  }

  const generateHotelQRCode = async () => {
    const fatherPluginspace = await getPluginSpace(true)

    if (!fatherPluginspace) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Não foi possível obter o pluginspace principal',
      })
      return
    }

    const store = hospitalityState.selectedStore
    if (!store) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        text: 'Selecione um estabelecimento',
      })
      return
    }

    if (!store.pluginspace?.hash) {
      Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        text: 'Estabelecimento sem hash configurado',
      })
      return
    }

    // Buscar o Father (pluginspace pai) para pegar o subdomain correto
    try {
      if (!store?.pluginspace?.subdomain) {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          text: 'Não foi possível obter o pluginspace principal',
        })
        return
      }

      // Determinar a URL baseada no ambiente usando o subdomain do Father
      let baseUrl = ''
      if (process.env.REACT_APP_API_URL === 'https://nis.ninegrid.com.br/api') {
        // Produção
        baseUrl =
          'https://webapp.ninegrid.com.br/' +
          window.btoa(store?.pluginspace?.subdomain) +
          `?father=${fatherPluginspace.id}}`
      } else {
        // Staging ou local
        baseUrl =
          'https://web-staging.ninegrid.com.br/' +
          window.btoa(store?.pluginspace?.subdomain) +
          `?father=${fatherPluginspace.id}`
      }

      const qrCodeData = {
        type: '',
        label: store.pluginspace?.name || 'ESTABELECIMENTO',
        iniInterval: 1,
        endInterval: 1,
        intervals: [],
        url: baseUrl,
        hash: store.pluginspace.hash, // Hash do child (estabelecimento)
      }

      const data = btoa(JSON.stringify(qrCodeData))
      const encodedData = encodeURIComponent(data)
      if (encodedData.length > 900) {
        const cacheKey = `qr-cache-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
        localStorage.setItem(cacheKey, data)
        window.open(`/webapp/generate-qr-code/${cacheKey}`)
      } else {
        window.open(`/webapp/generate-qr-code/${encodedData}`)
      }
    } catch (error) {
      console.log(error)
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Erro ao gerar QR Code',
      })
    }
  }

  // ============================
  // FUNÇÕES ESPECÍFICAS PARA OUTROS PLUGINS
  // ============================

  const otherPluginsLogic = useOtherPluginsLogic(commonState, commonFunctions, {
    showHotelStoresModal,
  })

  // ============================
  // COMPONENTE PRINCIPAL
  // ============================

  const getApps = async () => {
    try {
      setLoading(true)

      const thisPluginspace = await getPluginSpace(true)

      const isWholeSaler = thisPluginspace.wholeSaler

      const sectors = sectorOptions.map((s) => s)

      const modalities = await getModalities()
      const haveCounter = modalities.find(
        (modality) => modality.code === 'counter' && modality.status === true
      )
      const haveInternalDelivery = modalities.find(
        (modality) => modality.code === 'internal-delivery' && modality.status === true
      )
      if (usingSynkar) sectors.push({select: 'Synkar', value: 'synkar'})

      if (haveCounter) sectors.push({select: haveCounter.name, value: haveCounter.code})

      if (haveInternalDelivery)
        sectors.push({select: haveInternalDelivery.name, value: haveInternalDelivery.code})

      setSectorOptions(sectors)

      if (loginInfo.plugintype === 'hospitality') {
        setSectorOptions([
          {select: 'Selecione um setor', value: ''},
          {select: 'Quarto', value: 'room'},
        ])
      }

      const appRes = await GET('/appconfig')

      const filterWebapp =
        getPlugintypeIdByCode(loginInfo.plugintype) === 21
          ? (webapp: WebappResponseType) => webapp.type === 'qr'
          : (webapp: WebappResponseType) =>
              webapp.type === 'qr' ||
              webapp.type === 'seller' ||
              (webapp.type === 'qr_whole_sale' && isWholeSaler)

      const newWebapps: WebappType[] = appRes.data
        .filter(filterWebapp)
        .map((webapp: WebappResponseType) => {
          let url = ''
          // let hash = webapp.hash
          url = otherPluginsLogic.buildWebappUrl(webapp)

          const title = otherPluginsLogic.buildWebappTitle(webapp, isWholeSaler)

          let dataFinal: any = {
            id: webapp.id,
            title: title,
            buttons: null,
            webappLink: url,
            type: webapp.type,
          }

          dataFinal.buttons = otherPluginsLogic.buildWebappButtons(webapp, url)

          return dataFinal
        })

      if (thisPluginspace.type === 'hospitality') {
        const webapp = appRes.data[0]
        webapp.type = 'hotel-os'
        webapp.name = webapp.name + ' - Order Services'
        let url = ''
        // let hash = webapp.hash
        url = otherPluginsLogic.buildWebappUrl(webapp)

        const title = otherPluginsLogic.buildWebappTitle(webapp, isWholeSaler)

        const hotelOsWebapp: any = {
          id: newWebapps[0].id + 1,
          title: title,
          buttons: [],
          webappLink: url,
          type: webapp.type,
        }
        newWebapps.push(hotelOsWebapp)
      }

      setWebapps(newWebapps)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  async function loadPage() {
    setLoading(true)
    await getApps()
  }

  useEffect(() => {
    loadPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ============================
  // RENDERIZAÇÃO PRINCIPAL
  // ============================

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          {loginInfo.plugintype === 'hospitality' ? (
            <>
              <div className='row g-5 gx-xxl-12'>
                <div className='card card-xxl-stretch mb-5 mb-xl-12'>
                  <div className='card-header py-5'>
                    <h3 className='card-title align-items-start flex-column'>
                      <span className='card-label fw-bolder fs-3 mb-1'>Webapp</span>
                      <BreadCrumbs />
                    </h3>
                  </div>
                  <div className='card-body'>
                    {webapps
                      .filter((webapp) => ['qr', 'hotel-os'].includes(webapp.type))
                      .map((webapp, index) => (
                        <WebAppInfoHotel
                          key={`${webapp.title} - ${index}`}
                          title={webapp.title}
                          buttons={webapp.buttons}
                          webappLink={webapp.webappLink}
                          showQrCode={webapp.type !== 'table' && webapp.type !== 'card'}
                        />
                      ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className='row g-5 gx-xxl-12'>
                <div className='card card-xxl-stretch mb-5 mb-xl-12'>
                  <div className='card-header py-5'>
                    <h3 className='card-title align-items-start flex-column'>
                      <span className='card-label fw-bolder fs-3 mb-1'>Webapp</span>
                      <BreadCrumbs />
                    </h3>
                  </div>
                  <div className='card-body'>
                    {webapps.map((webapp, index) => (
                      <WebappInfo
                        key={`${webapp.title} - ${index}`}
                        title={webapp.title}
                        buttons={webapp.buttons}
                        webappLink={webapp.webappLink}
                        showQrCode={webapp.type !== 'table' && webapp.type !== 'card'}
                      />
                    ))}
                  </div>
                </div>
                <ModalWrapper
                  confirmAction={() => (actions === 'generateQrcode' ? generateQRCode() : '')}
                  textAction={actions === 'generateQrcode' ? 'Gerar QR Code(s)' : 'Ok'}
                  noAction={
                    qrCode.label === '' &&
                    (qrCode.type === 'table' ||
                      qrCode.type === 'card' ||
                      qrCode.type === 'internal-delivery')
                  }
                  disableMainButton={
                    qrCode.endInterval === undefined ||
                    qrCode.iniInterval === undefined ||
                    qrCode.iniInterval <= 0 ||
                    qrCode.endInterval < qrCode.iniInterval ||
                    (qrCode.intervals.length > 0 &&
                      qrCode.intervals.some(
                        (interval) =>
                          interval.endInterval === undefined ||
                          interval.iniInterval === undefined ||
                          interval.iniInterval <= 0 ||
                          interval.endInterval < interval.iniInterval
                      ))
                  }
                >
                  <div className='d-flex justify-content-between'>
                    <div className='w-100'>
                      <div>
                        <Input
                          inputAttr={{
                            type: 'select',
                          }}
                          label='Setor'
                          options={sectorOptions}
                          value={qrCode.type}
                          change={(value: string) => {
                            let _qrCode = {...qrCode}
                            _qrCode.type = value
                            if (value === 'card') _qrCode.label = 'COMANDA'
                            else if (value === 'table') _qrCode.label = 'MESA'
                            else _qrCode.label = ''
                            setQRCode(_qrCode)
                          }}
                        />
                        {qrCode.type && qrCode.type !== 'synkar' && qrCode.type !== 'counter' && (
                          <Input
                            inputAttr={{
                              type: 'default',
                            }}
                            label='Label QR Code'
                            value={qrCode.label}
                            change={(value: string) => {
                              let _qrCode = {...qrCode}
                              _qrCode.label = value
                              setQRCode(_qrCode)
                            }}
                            alarm={!qrCode.label}
                          />
                        )}
                      </div>
                      {qrCode.type !== 'synkar' && qrCode.type !== 'counter' && qrCode.type && (
                        <>
                          <div className='d-flex justify-content-between align-items-center'>
                            <div className='me-5 w-50'>
                              <Input
                                inputAttr={{
                                  type: 'number',
                                }}
                                label='Índice inicial'
                                value={qrCode.iniInterval}
                                change={(value: string) => {
                                  let _qrCode = {...qrCode}
                                  _qrCode.iniInterval = value ? parseInt(value) : undefined
                                  if (
                                    _qrCode.iniInterval &&
                                    _qrCode.endInterval &&
                                    _qrCode.iniInterval > _qrCode.endInterval
                                  ) {
                                    _qrCode.endInterval = _qrCode.iniInterval
                                  }
                                  setQRCode(_qrCode)
                                }}
                              />
                            </div>
                            <div className='me-5 w-50'>
                              <Input
                                inputAttr={{
                                  type: 'number',
                                }}
                                label='Índice final'
                                value={qrCode.endInterval}
                                minNumber={qrCode.iniInterval}
                                change={(value: string) => {
                                  let _qrCode = {...qrCode}
                                  _qrCode.endInterval = value ? parseInt(value) : undefined
                                  setQRCode(_qrCode)
                                }}
                              />
                            </div>
                            <div className='me-5 w-50'>
                              <div className='mt-6'>
                                <label className='form-label fw-normal'></label>
                                <div className='d-flex align-items-center'>
                                  <Button
                                    text='Adicionar Intervalo'
                                    color='light-primary'
                                    click={(event) => {
                                      addRange()
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          {maxRange && (
                            <div
                              className='d-flex flex-row align-items-center mt-4 p-2 gap-4'
                              style={{
                                border: '1px solid #EB5353',
                                borderRadius: '8px',
                                backgroundColor: '#FDE2E2',
                              }}
                            >
                              <i
                                className='fa fa-exclamation-triangle'
                                style={{color: '#EB5353'}}
                              ></i>
                              <p className='m-0'>{i18n.t('smart_order.limit_range')}</p>
                            </div>
                          )}
                          {qrCode.intervals.map((interval, i) => (
                            <div
                              key={`range-${i}`}
                              className='d-flex justify-content-between align-items-center'
                            >
                              <div className='me-5 w-100'>
                                <Input
                                  inputAttr={{
                                    type: 'number',
                                  }}
                                  label='Índice inicial'
                                  value={interval.iniInterval}
                                  change={(value: string) => {
                                    let _qrCode = {...qrCode}
                                    if (_qrCode.intervals[i]) {
                                      _qrCode.intervals[i].iniInterval = value
                                        ? parseInt(value)
                                        : undefined
                                      const currentInterval = _qrCode.intervals[i]

                                      if (
                                        currentInterval.iniInterval !== undefined &&
                                        currentInterval.endInterval !== undefined &&
                                        currentInterval.iniInterval > currentInterval.endInterval
                                      ) {
                                        _qrCode.intervals[i].endInterval =
                                          currentInterval.iniInterval
                                      }
                                      setQRCode(_qrCode)
                                    }
                                  }}
                                />
                              </div>
                              <div className='me-5 w-100'>
                                <Input
                                  inputAttr={{
                                    type: 'number',
                                  }}
                                  label='Índice final'
                                  value={interval.endInterval}
                                  minNumber={qrCode.iniInterval}
                                  change={(value: string) => {
                                    let _qrCode = {...qrCode}
                                    if (_qrCode.intervals[i]) {
                                      _qrCode.intervals[i].endInterval = value
                                        ? parseInt(value)
                                        : undefined
                                      setQRCode(_qrCode)
                                    }
                                  }}
                                />
                              </div>
                              <div className='me-5 py-5'>
                                <div className='mt-6'>
                                  <label className='form-label fw-normal'></label>
                                  <div className='d-flex align-items-center'>
                                    <Button
                                      icon='fas fa-trash'
                                      color='danger'
                                      size='small'
                                      click={() => removeRange(i)}
                                      text={''}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </ModalWrapper>
              </div>
            </>
          )}
          {loginInfo.plugintype === 'hospitality' && actions === 'hotelStores' && (
            <ModalWrapper
              confirmAction={() => generateHotelQRCode()}
              textAction='Gerar QR Code'
              noAction={!hospitalityState.selectedStore}
            >
              <div className='d-flex flex-column gap-4'>
                <Input
                  inputAttr={{
                    type: 'select',
                  }}
                  label='Selecione o Estabelecimento'
                  options={[
                    {select: 'Selecione um estabelecimento', value: 0},
                    ...hospitalityState.hotelStores
                      .filter((store) => store.status)
                      .map((store) => ({
                        select: `${store.pluginspace?.name || store.description} - ${store.code}`,
                        value: store.id,
                      })),
                  ]}
                  value={hospitalityState.selectedStore?.id || 0}
                  change={(value: string) => {
                    const numValue = typeof value === 'string' ? parseInt(value) : value
                    const store = hospitalityState.hotelStores.find((s) => s.id === numValue)
                    hospitalityState.setSelectedStore(store || null)
                  }}
                />
              </div>
            </ModalWrapper>
          )}
        </>
      )}
    </>
  )
}

const Webapp: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={[{title: 'Webapp', path: 'webapp', isActive: true}]}>
        Webapp
      </PageTitle>
      <WebappContent />
    </>
  )
}

export {Webapp}
